import { useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import MediaUploader from "./MediaUploader";

// Option lists (can be lifted out or injected via props if needed)
const SIZES = ["150ml", "175ml", "200ml","250ml", "500ml", "750ml", "1L", "1.25L", "1.5L", "1.75L", "1.85L", "2L"];
const UNIT_TYPES = ["bottle", "case"];

// Yup validation schema
const ProductSchema = Yup.object({
  name: Yup.string()
    .trim()
    .min(2, "Min 2 characters")
    .max(120)
    .required("Product name is required"),
  brand: Yup.string()
    .trim()
    .min(2, "Min 2 characters")
    .max(120)
    .required("Brand is required"),
  size_label: Yup.string()
    .oneOf(SIZES, "Invalid size")
    .required("Select a size"),
  unit_type: Yup.string()
    .oneOf(UNIT_TYPES, "Invalid unit type")
    .required("Select a unit"),
  case_pack_qty: Yup.number()
    .typeError("Enter a valid number")
    .integer("Must be a whole number")
    .min(1, "Must be at least 1")
    .max(10000, "Too large")
    .required("Case pack quantity is required"),
  base_price_per_case: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .max(1_000_000, "Unrealistic value")
    .required("Base price per case is required"),
  derived_unit_price: Yup.number()
    .typeError("Invalid")
    .min(0, "Cannot be negative")
    .max(1_000_000, "Unrealistic value")
    .required(),
  category_id: Yup.mixed().required("Select a category"),
  tax_rate: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Min 0%")
    .max(100, "Max 100%")
    .required("Tax rate is required"),
  active: Yup.boolean().required(),
  barcode: Yup.string().trim().max(64),
  sku: Yup.string().trim().max(64, "Max 64 characters"),
  owner_id: Yup.number()
    .typeError("Enter a valid number")
    .integer("Must be an integer")
    .min(1, "Must be at least 1"),
  media: Yup.array().of(Yup.mixed()).default([]),
});

const CATEGORIES = [
  { id: 1, name: "Beverages" },
  { id: 2, name: "Food" },
];

export default function ProductForm({
  form, // initial values object
  onSubmit, // (values) => void
  onCancel, // optional
  editing = false,
  submitting = false,
}) {
  // Compute derived price using the current values
  const computeDerived = (values) => {
    const cpq = Number(values.case_pack_qty || 0);
    const base = Number(values.base_price_per_case || 0);
    if (!cpq || cpq <= 0) return 0;
    const val = base / cpq;
    if (!isFinite(val) || val < 0) return 0;
    return Number(val.toFixed(4));
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: form?.name ?? "",
      brand: form?.brand ?? "",
      size_label: form?.size_label ?? "",
      unit_type: form?.unit_type ?? "",
      case_pack_qty: form?.case_pack_qty ?? "",
      base_price_per_case: form?.base_price_per_case ?? "",
      derived_unit_price: form?.derived_unit_price ?? 0,
      category_id: form?.category_id ?? "",
      tax_rate: form?.tax_rate ?? 0,
      active: !!form?.active,
      barcode: form?.barcode ?? "",
      sku: form?.sku ?? "",
      owner_id: form?.owner_id ?? "",
      media: Array.isArray(form?.media) ? form.media : [],
    },
    validationSchema: ProductSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      const cleaned = {
        ...values,
        case_pack_qty: values.case_pack_qty
          ? Number(values.case_pack_qty)
          : null,
        base_price_per_case: values.base_price_per_case
          ? parseFloat(values.base_price_per_case)
          : null,
        derived_unit_price: values.derived_unit_price
          ? parseFloat(values.derived_unit_price)
          : null,
        tax_rate: values.tax_rate ? parseFloat(values.tax_rate) : 0,
        owner_id: values.owner_id ? Number(values.owner_id) : null,
        active: values.active ? 1 : 0,
      };
      onSubmit?.(cleaned);
    },
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
  } = formik;

  // Keep derived price in sync whenever inputs that affect it change
  const derived = useMemo(
    () => computeDerived(values),
    [values.case_pack_qty, values.base_price_per_case]
  );
  useEffect(() => {
    if (Number(values.derived_unit_price) !== Number(derived)) {
      setFieldValue("derived_unit_price", derived, false); // avoid double-validation thrash
    }
  }, [derived]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = editing ? "Update Product" : "Create Product";

  const fieldClass = (name) =>
    `w-full p-3 border rounded transition ${
      touched[name] && errors[name]
        ? "border-red-500 ring-1 ring-red-200"
        : "border-gray-300 focus:border-gray-400 focus:ring-2 focus:ring-gray-200"
    }`;

  const hint = (name, text) => (
    <p id={`${name}-hint`} className="text-sm text-gray-500 mt-1">
      {text}
    </p>
  );

  const err = (name) =>
    touched[name] && errors[name] ? (
      <p role="alert" className="text-sm text-red-600 mt-1">
        {errors[name]}
      </p>
    ) : null;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="bg-white shadow-xl rounded-2xl p-8 mb-8"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-semibold text-gray-900">
          {editing ? "Edit Product" : "Create Product"}
        </h3>
        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
          {editing ? "Editing" : "New"}
        </span>
      </div>

      {/* Basic info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="name" className="font-medium text-gray-800">
            Product Name<span className="text-red-500"> *</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("name")}
            aria-invalid={!!(touched.name && errors.name)}
            aria-describedby="name-hint"
            placeholder="e.g., Coca-Cola Classic"
          />
          {hint("name", "Clear, searchable name — avoid internal codes here.")}
          {err("name")}
        </div>

        <div>
          <label htmlFor="brand" className="font-medium text-gray-800">
            Brand<span className="text-red-500"> *</span>
          </label>
          <input
            id="brand"
            name="brand"
            type="text"
            value={values.brand}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("brand")}
            aria-invalid={!!(touched.brand && errors.brand)}
            aria-describedby="brand-hint"
            placeholder="e.g., Coca-Cola"
          />
          {hint("brand", "Manufacturer or label displayed to retailers.")}
          {err("brand")}
        </div>

        <div>
          <label htmlFor="size_label" className="font-medium text-gray-800">
            Size<span className="text-red-500"> *</span>
          </label>
          <select
            id="size_label"
            name="size_label"
            value={values.size_label}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("size_label")}
            aria-invalid={!!(touched.size_label && errors.size_label)}
          >
            <option value="">Select Size</option>
            {SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {err("size_label")}
        </div>

        <div>
          <label htmlFor="unit_type" className="font-medium text-gray-800">
            Unit Type<span className="text-red-500"> *</span>
          </label>
          <select
            id="unit_type"
            name="unit_type"
            value={values.unit_type}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("unit_type")}
            aria-invalid={!!(touched.unit_type && errors.unit_type)}
          >
            <option value="">Unit Type</option>
            {UNIT_TYPES.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          {err("unit_type")}
        </div>

        <div>
          <label htmlFor="case_pack_qty" className="font-medium text-gray-800">
            Case Pack Qty<span className="text-red-500"> *</span>
          </label>
          <input
            id="case_pack_qty"
            name="case_pack_qty"
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={values.case_pack_qty}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("case_pack_qty")}
            aria-invalid={!!(touched.case_pack_qty && errors.case_pack_qty)}
            placeholder="e.g., 24"
          />
          {hint(
            "case_pack_qty",
            "Total individual units per case (bottles/cans)."
          )}
          {err("case_pack_qty")}
        </div>

        <div>
          <label
            htmlFor="base_price_per_case"
            className="font-medium text-gray-800"
          >
            Selling Price / Case<span className="text-red-500"> *</span>
          </label>
          <input
            id="base_price_per_case"
            name="base_price_per_case"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={values.base_price_per_case}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("base_price_per_case")}
            aria-invalid={
              !!(touched.base_price_per_case && errors.base_price_per_case)
            }
            placeholder="e.g., 192.00"
          />
          {err("base_price_per_case")}
        </div>

        <div>
          <label
            htmlFor="derived_unit_price"
            className="font-medium text-gray-800"
          >
            Derived Unit Price
          </label>
          <input
            id="derived_unit_price"
            name="derived_unit_price"
            type="number"
            inputMode="decimal"
            readOnly
            value={values.derived_unit_price}
            className="w-full p-3 border rounded bg-gray-100 border-gray-300 cursor-not-allowed"
            aria-readonly="true"
          />
          <p className="text-sm text-gray-500 mt-1">
            Auto-calculated: Base / Case Pack.
          </p>
          {err("derived_unit_price")}
        </div>

        <div>
          <label htmlFor="category_id" className="font-medium text-gray-800">
            Category<span className="text-red-500"> *</span>
          </label>
          <select
            id="category_id"
            name="category_id"
            value={values.category_id}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("category_id")}
            aria-invalid={!!(touched.category_id && errors.category_id)}
          >
            <option value="">Select Category</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {err("category_id")}
        </div>

        <div>
          <label htmlFor="tax_rate" className="font-medium text-gray-800">
            Tax Rate %<span className="text-red-500"> *</span>
          </label>
          <input
            id="tax_rate"
            name="tax_rate"
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step="0.01"
            value={values.tax_rate}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("tax_rate")}
            aria-invalid={!!(touched.tax_rate && errors.tax_rate)}
            placeholder="e.g., 18"
          />
          {err("tax_rate")}
        </div>

        <div>
          <label htmlFor="barcode" className="font-medium text-gray-800">
            Barcode (optional)
          </label>
          <input
            id="barcode"
            name="barcode"
            type="text"
            value={values.barcode}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("barcode")}
            placeholder="EAN/UPC"
          />
          {err("barcode")}
        </div>

        <div>
          <label htmlFor="sku" className="font-medium text-gray-800">
            SKU
          </label>
          <input
            id="sku"
            name="sku"
            type="text"
            value={values.sku}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("sku")}
            placeholder="Internal stock keeping unit"
          />
          {err("sku")}
        </div>

        <div>
          <label htmlFor="owner_id" className="font-medium text-gray-800">
            Owner ID
          </label>
          <input
            id="owner_id"
            name="owner_id"
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={values.owner_id}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldClass("owner_id")}
            aria-invalid={!!(touched.owner_id && errors.owner_id)}
            placeholder="e.g., 1"
          />
          {err("owner_id")}
        </div>

        <div className="flex items-center gap-3 mt-2">
          <input
            id="active"
            name="active"
            type="checkbox"
            checked={!!values.active}
            onChange={(e) => setFieldValue("active", e.target.checked)}
            onBlur={handleBlur}
            className="h-4 w-4"
          />
          <label htmlFor="active" className="font-medium text-gray-800">
            Active
          </label>
        </div>
        {err("active")}
      </div>

      {/* Media */}
      <div className="mt-8">
        <h4 className="font-semibold text-gray-900 mb-2">Images / Docs</h4>
        <MediaUploader
          value={values.media || []}
          onChange={(v) => setFieldValue("media", v)}
        />
      </div>

      {/* Actions */}
      <div className="mt-8 flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={submitting || isSubmitting}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {label}
        </button>
        {editing && (
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-100 text-gray-900 px-6 py-2 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
